import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from 'react-router-dom';
import Swal from "sweetalert2";

import PropertyGridView from './propertyListComponents/PropertyGridView';
import PropertyListView from './propertyListComponents/PropertyListView';
import ViewToggle from '../Common/ViewToggle';
import TableFooter from '../Common/TableFooter';
import CommonExportButtons from '../Common/CommonExportButtons';
import NoDataFound from '../Common/NoDataFound';
import PageSizeSelector from '../Common/PageSizeSelector';
import Filter from './components/Filter';

import { getPropertyFiltersAction, getPropertiesAction, deletePropertyAction } from '../../../store/actions/property/PropertyActions';

const PropertyList = () => {
    const dispatch = useDispatch();
    const loc = useLocation();
    const listingTypeRef = useRef({
        isPending: false,
        isBlocked: false, 
        isSold: false,
        isFeaturedMenu: false,
        isVerifiedMenu: false,
        isRejected: false
    });
    
    const isPending = loc.pathname.includes('pending-property-list');
    const isBlocked = loc.pathname.includes('blocked-property-list');
    const isSold = loc.pathname.includes('sold-property-list');
    const isFeaturedMenu = loc.pathname.includes('featured-property-list');
    const isVerifiedMenu = loc.pathname.includes('verified-property-list');
    const isRejected = loc.pathname.includes('rejected-property-list');
    const [priceRange, setPriceRange] = useState([0, 0]);
    const [areaRange, setAreaRange] = useState([0, 0]);
    const [openMenu, setOpenMenu] = useState(false);

    // const [formErrors, setFormErrors] = useState({});
    const { properties, loading } = useSelector((state) => state.property);
    const propertyRows = properties?.rows || [];
	const totalPages = properties?.pages || 1;
    const [searchTerm, setSearchTerm] = useState('');
    const [propertyTypeId, setPropertyTypeId] = useState('');
    const [listingTypeId, setListingTypeId] = useState('');
    const [location, setLocation] = useState({
        'lattitude' : '',
        'longitude' : ''
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(12);
    const { filters } = useSelector((state) => state.property);
    const [propertyTypeOptions, setPropertyTypeOptions] = useState([]);
    const [listingTypeOptions, setListingTypeOptions] = useState([]);
    const [isVerified, setIsVerified] = useState(false);
    const [isFeatured, setIsFeatured] = useState(false);
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState('desc');

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!"
        });

        if (result.isConfirmed) {
            const response = await dispatch(deletePropertyAction(id));
            if (response.data.success) {
                dispatch(getPropertiesAction(currentPage, pageSize));
                Swal.fire("Deleted!", "The property has been deleted.", "success");
                dispatch(getPropertiesAction(currentPage, pageSize));
            } else {
                Swal.fire("Failed!", "Something went wrong.", "warning");
            }
        }
    }

    const onPlaceSelected = async (place) => {
        const updated = {
            latitude: '',
            longitude: ''
        };
        if (place.geometry?.location) {
            updated.latitude = place.geometry.location.lat();
            updated.longitude = place.geometry.location.lng();
        }
        setLocation(updated)
    }

    const buildSearchQuery = () => {
        const near = location && location.latitude && location.longitude && areaRange ? `${location.latitude},${location.longitude},${areaRange[1]}` : '';
        const property = propertyTypeId ? propertyTypeId : '';
        const listing = listingTypeId ? listingTypeId : '';
        const price = priceRange && priceRange[1] > 0? `btw-${priceRange[0]},${priceRange[1]}` : '';
        const search = searchTerm ? searchTerm : '';

        const params = new URLSearchParams();

        if (near) params.append('filter_near', near);
        if (property) params.append('filter_propertyTypeId', property);
        if (listing) params.append('filter_listingTypeId', listing);
        if (price) params.append('filter_price', price);
        if (search) params.append('search', search);
        if (isVerified) params.append("filter_isVerified", true);
        if (isFeatured) params.append("filter_isFeatured", true);

        // Add page-specific status filters
        if(isPending) {
            params.append("filter_adStatus", "pending");
        } else if(isBlocked) {
            params.append("filter_adStatus", "blocked");
        } else if (isSold) {
            params.append("filter_adStatus", "sold");
        } else if (isFeaturedMenu) {
            params.append("filter_isFeatured", "true");
        } else if (isVerifiedMenu) {
            params.append("filter_isVerified", "true");
        } else if (isRejected) {
            params.append("filter_adStatus", "rejected");
        } else {
            params.append("filter_adStatus", "active");
        }

        // Add sorting parameters
        if (sortField) {
            params.append('orderBy', sortField);
            params.append('orderByDir', sortDirection);
        }

        const queryString = `${params.toString()}`;
        return queryString;
    };

    const onSubmit = async () => {
        let filterQuery = buildSearchQuery();
        dispatch(getPropertiesAction(currentPage, pageSize, searchTerm, filterQuery));
    }

    const handleViewChange = (newViewMode) => {
        setViewMode(newViewMode);
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
        } else {
            setSortField(field);
            setSortDirection('desc');
        }
        setCurrentPage(1); // Reset to first page when sorting
    };

    // Preprocess data for export to handle N/A values
    const processedExportData = propertyRows.map(item => {
        const customer = item.customerId || {};
        return {
            title: item.title || '',
            price: item.price || '',
            address: item.address || '',
            listingType: item.listingTypeId?.name || '',
            areaSize: item.areaSize || '',
            customerName: customer.name || '',
            customerEmail: customer.email || ' ',  // Add spaces around em dash for better centering
            customerPhone: customer.phone || ' ',  // Add spaces around em dash for better centering
            isFeatured: item.isFeatured === true ? 'true' : item.isFeatured === false ? 'false' : '',
            isVerified: item.isVerified === true ? 'true' : item.isVerified === false ? 'false' : '',
            createdAt: item.createdAt || ''
        };
    });

    useEffect(() => {
        if (filters && Object.keys(filters).length > 0) {
            setPropertyTypeOptions(filters.propertyTypes.map(type => ({ value: type._id, label: type.name })));
            setListingTypeOptions(filters.listingTypes.map(type => ({ value: type._id, label: type.name })));
        }
    }, [filters]);

    useEffect(() => {
        let action;
        let query = '';
        let pageToUse = currentPage;
        
        // Build base query with sorting
        if (sortField) {
            query = `orderBy=${sortField}&orderByDir=${sortDirection}`;
        }
        
        // Check if listing type changed, if so use page 1
        const currentListingType = { isPending, isBlocked, isSold, isFeaturedMenu, isVerifiedMenu, isRejected };
        const prevListingType = listingTypeRef.current;
        
        const listingTypeChanged = Object.keys(currentListingType).some(key => 
            currentListingType[key] !== prevListingType[key]
        );
        
        if (listingTypeChanged) {
            pageToUse = 1;
            setCurrentPage(1);
            listingTypeRef.current = currentListingType;
        }
        
        if(isPending) {
            const fullQuery = query ? `filter_adStatus=pending&${query}` : 'filter_adStatus=pending';
            action = getPropertiesAction(pageToUse, pageSize, "", fullQuery);
        } else if(isBlocked) {
            const fullQuery = query ? `filter_adStatus=blocked&${query}` : 'filter_adStatus=blocked';
            action = getPropertiesAction(pageToUse, pageSize, "", fullQuery);
        } else if (isSold) {
            const fullQuery = query ? `filter_adStatus=sold&${query}` : 'filter_adStatus=sold';
            action = getPropertiesAction(pageToUse, pageSize, "", fullQuery);
        } else if (isFeaturedMenu) {
            const fullQuery = query ? `filter_isFeatured=true&${query}` : 'filter_isFeatured=true';
            action = getPropertiesAction(pageToUse, pageSize, "", fullQuery);
        } else if (isVerifiedMenu) {
            const fullQuery = query ? `filter_isVerified=true&${query}` : 'filter_isVerified=true';
            action = getPropertiesAction(pageToUse, pageSize, "", fullQuery);
        } else if (isRejected) {
            const fullQuery = query ? `filter_adStatus=rejected&${query}` : 'filter_adStatus=rejected';
            action = getPropertiesAction(pageToUse, pageSize, "", fullQuery);
        } else {
            const fullQuery = query ? `filter_adStatus=active&${query}` : 'filter_adStatus=active';
            action = getPropertiesAction(pageToUse, pageSize, "", fullQuery);
        }
        dispatch(action);
    }, [dispatch, currentPage, pageSize, isPending, isBlocked, isSold, isFeaturedMenu, isVerifiedMenu, isRejected, sortField, sortDirection]);
    
    useEffect(() => {
        dispatch(getPropertyFiltersAction());
    }, [dispatch]);

    return (
        <>
            <Filter 
                setOpenMenu={setOpenMenu}
                openMenu={openMenu}
                propertyTypeOptions={propertyTypeOptions}
                listingTypeOptions={listingTypeOptions}
                priceRange={priceRange}
                areaRange={areaRange}
                setPriceRange={setPriceRange}
                setAreaRange={setAreaRange}
                onPlaceSelected={onPlaceSelected}
                onSubmit={onSubmit}
                setSearchTerm={setSearchTerm}
                setPropertyTypeId={setPropertyTypeId}
                setListingTypeId={setListingTypeId}
                setLocation={setLocation}
                isFeatured={isFeatured}
                setIsFeatured={setIsFeatured}
                isVerified={isVerified}
                setIsVerified={setIsVerified}
            />
            <div className="d-flex justify-content-between align-items-center mb-3">
                <PageSizeSelector 
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    setCurrentPage={setCurrentPage}
                    options={[12, 24, 50, 100]}
                />
                <div className="d-flex align-items-center gap-2">
                    <CommonExportButtons 
                        data={processedExportData} 
                        exportHeaders={[
                            { label: 'Title', key: 'title' },
                            { label: 'Price', key: 'price' },
                            { label: 'Address', key: 'address' },
                            { label: 'Listing Type', key: 'listingType' },
                            { label: 'Area Size', key: 'areaSize' },
                            { label: 'Customer Name', key: 'customerName' },
                            { label: 'Customer Email', key: 'customerEmail' },
                            { label: 'Customer Phone', key: 'customerPhone' },
                            { label: 'Is Featured', key: 'isFeatured' },
                            { label: 'Is Verified', key: 'isVerified' },
                            { label: 'Created At', key: 'createdAt' }
                        ]}
                        filename="properties"
                        pdfTitle="Properties List"
                        totalText="Total Properties"
                        pdfOrientation="landscape"
                        pdfHeaderColor={[52, 144, 220]}
                        includeTimestamp={true}
                        dateFields={['createdAt']}
                    />
                    <ViewToggle viewMode={viewMode} onViewChange={handleViewChange} />
                </div>
            </div>
            <div>
                <div>
                    {!loading && propertyRows.length === 0 ? (
                        <NoDataFound 
                            message="No Properties Found" 
                            description="No properties match your current filters. Try adjusting your search criteria or filters."
                            icon="fa fa-home"
                            minHeight={300}
                        />
                    ) : viewMode === 'grid' ? (
                        <PropertyGridView 
                            propertyRows={propertyRows} 
                            loading={loading} 
                            handleDelete={handleDelete} 
                        />
                    ) : (
                        <PropertyListView 
                            propertyRows={propertyRows} 
                            loading={loading} 
                            handleDelete={handleDelete}
                            onSort={handleSort}
                            sortField={sortField}
                            sortDirection={sortDirection}
                        />
                    )}
                </div>
            </div>
            {(!loading && propertyRows.length > 0) && (
                <TableFooter
                    currentPage={currentPage}
                    totalPages={totalPages}
                    pageSize={pageSize}
                    totalEntries={properties?.total || propertyRows.length}
                    onPageChange={setCurrentPage}
                />
            )}
        </>
    );
};

export default PropertyList;